/**
 * Wave FFFFF · ActivityFeed component.
 *
 * Renders a vertical timeline of ActivityItem values. Used both by
 * ProjectActivityPage (full feed with filter chips) and as a mini-widget
 * inside VisaoGeralPage / ProjectDetailsPage sidebar.
 *
 * Props:
 *   items           — array of ProjectActivityItem to display
 *   isLoading       — show skeleton rows
 *   isError         — show error message
 *   hasMore         — show "Carregar mais" button
 *   onLoadMore      — called when button clicked
 *   isLoadingMore   — spinner on the button
 *   maxItems        — when set, clamps display (widget mode)
 */

import type { ProjectActivityItem, ProjectActivityKind } from '@/api/client';
import { Link } from 'react-router-dom';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

// ─── Kind → icon + colour mapping ────────────────────────────────────────

const KIND_META: Record<
  ProjectActivityKind,
  { dot: string; icon: React.ReactNode }
> = {
  rdo_created: {
    dot: 'bg-brand-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12h6M9 16h4" />
      </svg>
    ),
  },
  po_created: {
    dot: 'bg-ok-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 4h2l2.5 11a2 2 0 002 1.5H17a2 2 0 002-1.5L21 7H6" />
        <circle cx="9" cy="20" r="1" /><circle cx="17" cy="20" r="1" />
      </svg>
    ),
  },
  po_received: {
    dot: 'bg-ok-500',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />
        <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
      </svg>
    ),
  },
  estimate_edited: {
    dot: 'bg-ink-subtle',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
  },
  estimate_committed: {
    dot: 'bg-brand-500',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
  },
  fvs_passed: {
    dot: 'bg-ok-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <path d="M22 4L12 14.01l-3-3" />
      </svg>
    ),
  },
  fvs_failed: {
    dot: 'bg-err-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6M9 9l6 6" />
      </svg>
    ),
  },
  rfi_opened: {
    dot: 'bg-warn-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
    ),
  },
  rfi_answered: {
    dot: 'bg-brand-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  rnc_created: {
    dot: 'bg-err-500',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <path d="M12 9v4M12 17h.01" />
      </svg>
    ),
  },
  photo_uploaded: {
    dot: 'bg-ink-subtle',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M5 7h3l2-2h4l2 2h3a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1z" />
        <circle cx="12" cy="13" r="3.5" />
      </svg>
    ),
  },
  override_committed: {
    dot: 'bg-warn-500',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
  },
  doc_uploaded: {
    dot: 'bg-ink-subtle',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M12 12v6M9 15l3-3 3 3" />
      </svg>
    ),
  },
  stage_completed: {
    dot: 'bg-ok-400',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 19V5M4 19h16M8 15v-4M12 15V8M16 15v-6" />
      </svg>
    ),
  },
  budget_changed: {
    dot: 'bg-brand-300',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatRelativeDay(iso: string, today: string, yesterday: string): string {
  const date = iso.slice(0, 10);
  const now = new Date();
  const nowDay = now.toISOString().slice(0, 10);
  const yDay = new Date(now);
  yDay.setDate(yDay.getDate() - 1);
  const yDayStr = yDay.toISOString().slice(0, 10);
  if (date === nowDay) return today;
  if (date === yDayStr) return yesterday;
  // Format dd/mm/yyyy
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}

function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

// ─── Skeleton row ─────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <li className="flex gap-3 py-2.5">
      <div className="mt-1 h-6 w-6 shrink-0 animate-pulse rounded-full bg-surface-2" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 w-2/3 animate-pulse rounded bg-surface-2" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-surface-2" />
      </div>
    </li>
  );
}

// ─── Single row ───────────────────────────────────────────────────────────

function ActivityRow({ item, todayLabel, yesterdayLabel }: { item: ProjectActivityItem; todayLabel: string; yesterdayLabel: string }) {
  const meta = KIND_META[item.kind];
  const time = formatTime(item.occurredAt);
  const dayLabel = formatRelativeDay(item.occurredAt, todayLabel, yesterdayLabel);

  const inner = (
    <div className="flex gap-3 py-2.5 group">
      {/* dot + icon */}
      <div className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center">
        <span className={cn('absolute inset-0 rounded-full opacity-15', meta?.dot ?? 'bg-ink-subtle')} />
        <span className={cn('text-current', meta?.dot ? '' : '')}>
          {meta?.icon ?? (
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
              <circle cx="12" cy="12" r="4" />
            </svg>
          )}
        </span>
      </div>

      {/* content */}
      <div className="min-w-0 flex-1">
        <p className={cn(
          'text-[13px] leading-snug text-ink',
          item.href ? 'group-hover:text-brand-400 transition-colors' : '',
        )}>
          {item.summary}
        </p>
        <p className="mt-0.5 text-[11.5px] text-ink-subtle">
          {item.actor?.userName ?? ''}
          {item.actor?.userName ? ' · ' : ''}
          {dayLabel} {time}
        </p>
      </div>
    </div>
  );

  if (item.href) {
    return (
      <li>
        <Link to={item.href} className="block -mx-2 rounded-lg px-2 hover:bg-surface-2 transition-colors">
          {inner}
        </Link>
      </li>
    );
  }
  return <li>{inner}</li>;
}

// ─── Public component ─────────────────────────────────────────────────────

export interface ActivityFeedProps {
  items: ProjectActivityItem[];
  isLoading?: boolean;
  isError?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** Clamp to N items (widget mode). */
  maxItems?: number;
  /** Show skeleton count when loading */
  skeletonCount?: number;
}

export function ActivityFeed({
  items,
  isLoading = false,
  isError = false,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  maxItems,
  skeletonCount = 5,
}: ActivityFeedProps) {
  const { t } = useT();
  const pt = t.projectActivity;

  const displayItems = maxItems ? items.slice(0, maxItems) : items;

  if (isLoading) {
    return (
      <ul className="divide-y divide-line/40">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </ul>
    );
  }

  if (isError) {
    return (
      <p className="py-6 text-center text-sm text-err">{pt.loadFailed}</p>
    );
  }

  if (displayItems.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-subtle">{pt.empty}</p>
    );
  }

  return (
    <div>
      <ul className="divide-y divide-line/40">
        {displayItems.map((item) => (
          <ActivityRow
            key={item.id}
            item={item}
            todayLabel={pt.today}
            yesterdayLabel={pt.yesterday}
          />
        ))}
      </ul>

      {hasMore && onLoadMore && !maxItems && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="pressable inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line bg-surface px-4 text-[13px] font-medium text-ink-muted hover:bg-surface-2 hover:text-ink disabled:opacity-50 transition-colors"
          >
            {isLoadingMore ? (
              <svg className="animate-spin" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 4v6h6" /><path d="M4 10a9 9 0 101.5-4.8" />
              </svg>
            ) : null}
            {pt.loadMore}
          </button>
        </div>
      )}
    </div>
  );
}
